/**
 * The styleguide: every primitive, in every state, in both directions.
 *
 * It exists to make two classes of defect visible that no unit test catches:
 *
 * **Mirroring.** A component that uses `margin-left` instead of `margin-inline-start` looks
 * perfect in English and wrong in Arabic, and nobody notices until an Arabic-speaking employee
 * says the interface feels broken without being able to say why. Rendering everything in both
 * directions on one page makes it a five-second check.
 *
 * **States nobody builds screens for.** Disabled, loading, error, empty, long text, a number
 * column, an Arabic string inside an English sentence. Each is a real state of a real screen, and
 * each is the one that ships broken because the happy path was what got looked at.
 *
 * It is shipped rather than kept in a branch, and it is not gated: there is nothing here but
 * components rendered with sample data, and a styleguide behind a login is a styleguide nobody
 * opens.
 */

import { useState } from "react";

import {
  Button,
  Card,
  CitationChip,
  EmptyState,
  FileDropzone,
  Input,
  Modal,
  Select,
  SidePanel,
  StatusPill,
  Table,
  Tabs,
  Textarea,
  ToastProvider,
  useToast,
  type StatusTone,
  type ToastTone,
} from "./index";

const TONES: StatusTone[] = ["success", "warning", "danger", "info", "neutral"];
const TOAST_TONES: ToastTone[] = ["success", "error", "warning", "info"];

interface Row {
  id: string;
  document: string;
  version: string;
  chunks: number;
  status: StatusTone;
}

const ROWS: Row[] = [
  { id: "1", document: "Local Transfer Procedure", version: "2.1", chunks: 6, status: "success" },
  { id: "2", document: "إجراء التحويل المحلي", version: "2.1", chunks: 5, status: "success" },
  { id: "3", document: "Fee Schedule 2026", version: "1.0", chunks: 12, status: "neutral" },
  { id: "4", document: "Scanned Circular 12/2024", version: "1.0", chunks: 0, status: "danger" },
];

/** Renders the whole guide once, in one direction. */
function Gallery({ dir, lang }: { dir: "ltr" | "rtl"; lang: "en" | "ar" }): JSX.Element {
  const toast = useToast();
  const [modalOpen, setModalOpen] = useState(false);
  const [panelOpen, setPanelOpen] = useState(true);
  const [tab, setTab] = useState("overview");
  const arabic = lang === "ar";

  const label = (english: string, عربي: string) => (arabic ? عربي : english);

  return (
    <div
      dir={dir}
      lang={lang}
      className="flex flex-col gap-6 rounded-md border border-borderSubtle bg-surface p-6"
      style={{ fontFamily: arabic ? "var(--nab-font-arabic)" : "var(--nab-font-sans)" }}
    >
      <h2 className="text-heading-md text-textPrimary">
        {label("Left to right — English", "من اليمين إلى اليسار — العربية")}
      </h2>

      <Card title={label("Buttons", "الأزرار")}>
        <div className="flex flex-wrap items-center gap-3">
          <Button variant="primary">{label("Publish", "نشر")}</Button>
          <Button variant="secondary">{label("Cancel", "إلغاء")}</Button>
          <Button variant="danger">{label("Retire", "سحب")}</Button>
          <Button variant="ghost">{label("Details", "تفاصيل")}</Button>
          <Button variant="primary" disabled>
            {label("Disabled", "معطّل")}
          </Button>
          <Button variant="primary" loading>
            {label("Indexing…", "جارٍ الفهرسة…")}
          </Button>
          <Button variant="secondary" size="sm">
            {label("Small", "صغير")}
          </Button>
          {/* An icon must sit on the reading-start side in both directions. */}
          <Button variant="secondary" icon={<span>↑</span>}>
            {label("Upload", "رفع")}
          </Button>
        </div>
      </Card>

      <Card title={label("Form fields", "حقول النموذج")}>
        <div className="grid gap-4 md:grid-cols-2">
          <Input label={label("Document title", "عنوان المستند")} placeholder={label("Fee Schedule 2026", "تعرفة الرسوم 2026")} />
          <Input
            label={label("Effective date", "ساري من")}
            hint={label("YYYY-MM-DD", "سنة-شهر-يوم")}
            defaultValue="2026-03-01"
          />
          <Input
            label={label("Document id", "معرّف المستند")}
            error={label("This id is already in use.", "هذا المعرّف مستخدم بالفعل.")}
            defaultValue="proc-local-transfer"
          />
          <Select
            label={label("Corpus", "المجموعة")}
            options={[
              { value: "internal", label: label("Internal", "داخلية") },
              { value: "public", label: label("Public", "عامة") },
            ]}
          />
          <Textarea
            label={label("Procedure text", "نص الإجراء")}
            hint={label("Markdown is supported.", "يدعم تنسيق ماركداون.")}
            defaultValue={label(
              "The local transfer fee at a branch counter is 7 LYD.",
              "عمولة التحويل المحلي في الفرع 7 دينار.",
            )}
          />
          <Textarea
            label={label("Annotation", "ملاحظة")}
            error={label("An annotation is required.", "الملاحظة مطلوبة.")}
          />
        </div>
      </Card>

      <Card title={label("Status", "الحالة")}>
        <div className="flex flex-wrap gap-2">
          {TONES.map((tone) => (
            <StatusPill key={tone} tone={tone}>
              {tone}
            </StatusPill>
          ))}
        </div>
        <p className="mt-4 text-body-md text-textPrimary">
          {label("The fee is 7 LYD", "العمولة 7 دينار")}
          <CitationChip index={1} label={label("Source 1", "المصدر 1")} />
          {label("and applies from March.", "وتسري اعتبارًا من مارس.")}
          <CitationChip index={2} label={label("Source 2", "المصدر 2")} active />
        </p>
      </Card>

      <Card title={label("Table", "الجدول")}>
        <Table
          caption={label("Indexed documents", "المستندات المفهرسة")}
          rowKey={(row) => row.id}
          rows={ROWS}
          columns={[
            {
              key: "document",
              header: label("Document", "المستند"),
              // bdi so an Arabic title inside an English table does not reorder the row.
              render: (row) => <bdi>{row.document}</bdi>,
            },
            { key: "version", header: label("Version", "الإصدار"), render: (row) => row.version },
            {
              key: "chunks",
              header: label("Passages", "المقاطع"),
              numeric: true,
              render: (row) => row.chunks,
            },
            {
              key: "status",
              header: label("Status", "الحالة"),
              render: (row) => <StatusPill tone={row.status}>{row.status}</StatusPill>,
            },
          ]}
        />
      </Card>

      <Card title={label("Empty states", "الحالات الفارغة")}>
        <div className="grid gap-4 md:grid-cols-2">
          <EmptyState
            title={label("No documents yet", "لا توجد مستندات بعد")}
            description={label(
              "Upload a procedure or write one directly to start.",
              "ارفع إجراءً أو اكتب واحدًا مباشرة للبدء.",
            )}
            action={<Button variant="primary">{label("Upload", "رفع")}</Button>}
          />
          <EmptyState
            title={label("Nothing in the queue", "لا شيء في قائمة المراجعة")}
            description={label("Every flag has been dealt with.", "تمت معالجة كل العلامات.")}
          />
        </div>
      </Card>

      <Card title={label("Tabs", "التبويبات")}>
        <Tabs
          label={label("Sections", "الأقسام")}
          active={tab}
          onChange={setTab}
          tabs={[
            { id: "overview", label: label("Overview", "نظرة عامة") },
            { id: "sources", label: label("Sources", "المصادر") },
            { id: "history", label: label("History", "السجل") },
          ]}
        />
        <p className="mt-3 text-body-sm text-textMuted">
          {label("Arrow keys move between tabs.", "أسهم لوحة المفاتيح تنقلك بين التبويبات.")}
        </p>
      </Card>

      <Card title={label("Upload", "الرفع")}>
        <FileDropzone
          label={label("Drop a file here, or choose one", "أفلت ملفًا هنا أو اختر ملفًا")}
          hint={label("PDF, DOCX or HTML, up to 64 MB", "PDF أو DOCX أو HTML، حتى 64 ميجابايت")}
          accept={[".pdf", ".docx", ".html"]}
          onFiles={() => undefined}
          rejectMessage={(name) =>
            label(`${name} is not an accepted file type.`, `${name} ليس نوع ملف مقبولًا.`)
          }
        />
      </Card>

      <Card title={label("Overlays", "الطبقات")}>
        <div className="flex flex-wrap gap-3">
          <Button variant="secondary" onClick={() => setModalOpen(true)}>
            {label("Open modal", "افتح النافذة")}
          </Button>
          <Button variant="secondary" onClick={() => setPanelOpen((open) => !open)}>
            {label("Toggle side panel", "أظهر/أخفِ اللوحة الجانبية")}
          </Button>
          {TOAST_TONES.map((tone) => (
            <Button
              key={tone}
              variant="ghost"
              onClick={() =>
                toast.show({
                  tone,
                  message: label(`A ${tone} toast`, `إشعار ${tone}`),
                  // Omitted rather than passed as undefined: `exactOptionalPropertyTypes`
                  // distinguishes "absent" from "explicitly nothing", and the type says absent.
                  ...(tone === "error"
                    ? {
                        detail: label(
                          "Errors stay until dismissed.",
                          "تبقى الأخطاء حتى تُغلق.",
                        ),
                      }
                    : {}),
                })
              }
            >
              {label(`Toast: ${tone}`, `إشعار: ${tone}`)}
            </Button>
          ))}
        </div>

        <div className="mt-4 flex gap-4">
          <SidePanel
            open={panelOpen}
            onClose={() => setPanelOpen(false)}
            title={label("Source", "المصدر")}
            closeLabel={label("Close", "إغلاق")}
          >
            <blockquote className="text-body-sm text-textMuted">
              {label(
                "The local transfer fee at a branch counter is 7 LYD for amounts up to 5,000 LYD.",
                "عمولة التحويل المحلي في الفرع 7 دينار للمبالغ حتى 5000 دينار.",
              )}
            </blockquote>
          </SidePanel>
        </div>

        <Modal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          title={label("Retire this document?", "سحب هذا المستند؟")}
          closeLabel={label("Close", "إغلاق")}
          footer={
            <>
              <Button variant="secondary" onClick={() => setModalOpen(false)}>
                {label("Cancel", "إلغاء")}
              </Button>
              <Button variant="danger" onClick={() => setModalOpen(false)}>
                {label("Retire", "سحب")}
              </Button>
            </>
          }
        >
          <p className="text-body-md">
            {label(
              "The topic reverts to the honest refusal. Nothing is deleted.",
              "يعود الموضوع إلى الرفض الصريح، ولا يُحذف شيء.",
            )}
          </p>
        </Modal>
      </Card>
    </div>
  );
}

export function Styleguide(): JSX.Element {
  // Self-contained: the guide brings its own toast region so it can be mounted anywhere without
  // the host app having to provide one.
  return (
    <ToastProvider regionLabel="Notifications" closeLabel="Dismiss">
      <StyleguideContent />
    </ToastProvider>
  );
}

function StyleguideContent(): JSX.Element {
  return (
    <main
      className="mx-auto flex max-w-content flex-col gap-8 bg-surface p-6 font-sans"
      // The guide can be mounted outside the app shell, so it inherits no font. Set explicitly
      // rather than assuming a parent that may not exist.
      style={{ fontFamily: "var(--nab-font-sans)" }}
    >
      {/* i18n-exempt: a developer-facing reference page, not part of either product. Translating
          it would imply it ships to employees, and the sample data below is bilingual anyway. */}
      <header>
        {/* i18n-exempt: developer reference page, never shown to an employee */}
        <h1 className="text-display-sm text-textPrimary">Design system</h1>
        {/* i18n-exempt: developer reference page, never shown to an employee */}
        <p className="max-w-prose text-body-md text-textMuted">
          Every primitive, in every state, in both directions. Compare the two columns: anything
          that does not mirror is a bug.
        </p>
      </header>

      <Gallery dir="ltr" lang="en" />
      <Gallery dir="rtl" lang="ar" />
    </main>
  );
}
